/**
 * LeadStatusSelect — the designed status dropdown for leads (2026 language,
 * same anatomy as the finance `pd2-status-select`): a status-tinted trigger +
 * a menu of option rows (square status tile · label · Check on the active one).
 *
 * The menu is portaled to <body> and positioned from the trigger rect so it is
 * never clipped by `.content-area { overflow: hidden }` or a scrolling list;
 * it flips upward when there is no room below. Closes on outside click /
 * touch, Escape, scroll and resize. Arrow keys move between options.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CaretDown, Check } from "@phosphor-icons/react";
import { statusStyleVars } from "./leadsUtils";

const MENU_GAP = 4;
const MENU_MIN_WIDTH = 220;

function LeadStatusSelect({
  value,
  statuses,
  status, // resolved status object for `value` (may be the "unknown" fallback)
  onChange, // (newValue) => void
  disabled = false,
  busy = false,
  size = "sm", // "sm" (list row) | "md" (detail modal)
  ariaLabel = "שינוי סטטוס",
  excludeValues = [],
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: MENU_MIN_WIDTH, up: false });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const options = statuses.filter((s) => !excludeValues.includes(s.value) || s.value === value);
  const isUnknown = !status || status.value === "__unknown";

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const estimated = Math.min(options.length, 8) * 40 + 12;
    const spaceBelow = vh - r.bottom - MENU_GAP;
    const up = spaceBelow < estimated && r.top > spaceBelow;
    const width = Math.max(r.width, MENU_MIN_WIDTH);
    // RTL: align the menu's END (left edge in RTL) with the trigger's left edge
    let left = r.left;
    if (left + width > vw - 8) left = Math.max(8, vw - 8 - width);
    setPos({ top: up ? r.top - MENU_GAP : r.bottom + MENU_GAP, left, width, up });
  }, [options.length]);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        const items = [...(menuRef.current?.querySelectorAll('[role="option"]:not(:disabled)') || [])];
        if (!items.length) return;
        e.preventDefault();
        const idx = items.indexOf(document.activeElement);
        const next = e.key === "ArrowDown" ? (idx + 1) % items.length : (idx - 1 + items.length) % items.length;
        items[next].focus();
      }
    };
    const onScrollOrResize = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [open]);

  // Focus the active option when the menu opens
  useEffect(() => {
    if (!open) return;
    const active = menuRef.current?.querySelector('[role="option"][aria-selected="true"]') || menuRef.current?.querySelector('[role="option"]');
    active?.focus?.();
  }, [open]);

  const pick = (v) => {
    setOpen(false);
    if (v !== value) onChange?.(v);
    triggerRef.current?.focus();
  };

  return (
    <div className={`ld-ss ld-ss-${size}`} style={statusStyleVars(status)}>
      <button
        ref={triggerRef}
        type="button"
        className={`ld-ss-trigger${open ? " is-open" : ""}${isUnknown ? " is-unknown" : ""}`}
        onClick={() => setOpen((v) => !v)}
        disabled={disabled || busy}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className="ld-ss-value">{status?.label || "לא ידוע"}</span>
        <CaretDown size={13} weight="bold" className={`ld-ss-caret${open ? " is-open" : ""}`} />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className={`ld-ss-menu${pos.up ? " is-up" : ""}`}
            role="listbox"
            aria-label={ariaLabel}
            dir="rtl"
            style={{ top: pos.top, left: pos.left, width: pos.width }}
          >
            {options.map((s) => {
              const isActive = s.value === value;
              return (
                <button
                  key={s.value}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={`ld-ss-option${isActive ? " is-active" : ""}`}
                  style={statusStyleVars(s)}
                  onClick={() => pick(s.value)}
                  disabled={isActive}
                >
                  <span className="ld-ss-option-tile" aria-hidden="true" />
                  <span className="ld-ss-option-label">{s.label}</span>
                  {isActive && <Check size={15} weight="bold" className="ld-ss-option-check" />}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
}

export default LeadStatusSelect;
